import React from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  FormDialogActions,
  FormDialogTitle,
  FormElement,
  FormInputsStack,
  FormCancelButton,
  useForm,
  RichTextEditor,
} from '@marageti/z4-lib';
import { Controller, SubmitHandler, FieldValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { TravelPreferenceSections } from '@marageti/z4-sdk/lib/people';
import { stripHtml } from './utils';

const schema = z.object({
  preferences: z.string(),
});

type EditTravelPreferencesModalProps = {
  open: boolean;
  onClose: () => void;
  categoryKey: keyof TravelPreferenceSections;
  categoryLabel: string;
  currentValue: string | undefined;
  defaultAsList?: boolean;
  onSave: (value: string) => void;
};

const EditTravelPreferencesModal = ({
  open,
  onClose,
  categoryKey,
  categoryLabel,
  currentValue,
  defaultAsList = true,
  onSave,
}: EditTravelPreferencesModalProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('desktop'));
  const initialValue = currentValue ?? '';
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      preferences: initialValue,
    },
  });

  const watchedPreferences = watch('preferences');
  const isUnchanged = (watchedPreferences ?? '') === initialValue;
  const isSaveDisabled = isUnchanged;

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    const value = data.preferences && stripHtml(data.preferences).trim() !== ''
      ? data.preferences
      : '';
    onSave(value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
    >
      <FormDialogTitle handleClose={onClose}>
        {categoryLabel}
      </FormDialogTitle>

      <FormElement name="edit-travel-preferences-form" onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ paddingTop: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These preferences improve trip matching.
          </Typography>
          <FormInputsStack>
            <Controller
              control={control}
              name="preferences"
              render={({ field }) => (
                <RichTextEditor
                  defaultValue={currentValue || (defaultAsList ? '<ul><li></li></ul>' : '')}
                  editorName={`travelPreferences-${categoryKey}`}
                  error={!!errors.preferences}
                  helperText={errors.preferences?.message as string}
                  label="Preferences"
                  placeholder=" "
                  onChange={field.onChange}
                  sx={{
                    maxHeight: { mobile: 'calc(100vh - 300px)', desktop: 'calc(100vh - 340px)' },
                    minHeight: '240px',
                    flex: 1,
                  }}
                  useLists
                />
              )}
            />
          </FormInputsStack>
        </DialogContent>
        <FormDialogActions>
          <FormCancelButton onClick={onClose} />
          <Button
            type="submit"
            variant="contained"
            disabled={isSaveDisabled}
            sx={{ width: { mobile: '100%', desktop: 'fit-content' } }}
          >
            Save Preferences
          </Button>
        </FormDialogActions>
      </FormElement>
    </Dialog>
  );
};

export default EditTravelPreferencesModal;
